import type { ItemStatus, ItemType, Priority, UserSummary } from '@/types/api';

/**
 * Traduccion entre los enums de la API y el lenguaje visual del diseno:
 * etiquetas en espanol, colores de columna e insignias de tipo.
 */

export const TYPE_META: Record<ItemType, { label: string; bg: string; fg: string; border: string }> = {
  STORY: { label: 'HISTORIA', bg: '#13233d', fg: '#8ab8f5', border: '#24406b' },
  BUG: { label: 'BUG', bg: '#331715', fg: '#f58c84', border: '#5c2b27' },
  TASK: { label: 'TAREA', bg: '#10281f', fg: '#4ed6a3', border: '#1c4534' },
  IMPROVEMENT: { label: 'MEJORA', bg: '#2e2210', fg: '#edbc63', border: '#4d3a17' },
  EPIC: { label: 'EPIC', bg: '#1f2045', fg: '#a5a5ff', border: '#3b3e78' },
  SPIKE: { label: 'SPIKE', bg: '#22243c', fg: '#adafc7', border: '#2f3153' },
};

export const STATUS_META: Record<ItemStatus, { label: string; color: string }> = {
  BACKLOG: { label: 'Backlog', color: '#8688a6' },
  TODO: { label: 'Por hacer', color: '#8688a6' },
  IN_PROGRESS: { label: 'En curso', color: '#6aa8f0' },
  IN_REVIEW: { label: 'En QA', color: '#e0a94a' },
  DONE: { label: 'Hecho', color: '#35c48f' },
};

/** Columnas del tablero, en el orden en que fluye el trabajo. */
export const BOARD_COLUMNS: ItemStatus[] = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE'];

/**
 * Prioridad de un ticket, de mas urgente a menos.
 *
 * Los tonos estan subidos respecto al color "puro" que pediria cada nombre
 * porque tienen que leerse sobre el fondo oscuro; `soft` y `line` son su
 * version tenue para el relleno y el borde de la insignia.
 */
export const PRIORITY_META: Record<
  Priority,
  { label: string; color: string; soft: string; line: string; order: number }
> = {
  CRITICAL: { label: 'Muy alta', color: '#f87171', soft: '#331715', line: '#5c2b27', order: 0 },
  HIGH: { label: 'Alta', color: '#fb923c', soft: '#2e1c0d', line: '#5a3717', order: 1 },
  MEDIUM: { label: 'Media', color: '#fbbf24', soft: '#2e2410', line: '#544017', order: 2 },
  LOW: { label: 'Baja', color: '#60a5fa', soft: '#13233d', line: '#24406b', order: 3 },
  LOWEST: { label: 'Muy baja', color: '#67e8f9', soft: '#0d2b33', line: '#1a505c', order: 4 },
};

/** De mas urgente a menos, para los selectores. */
export const PRIORITY_ORDER: Priority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'LOWEST'];

/**
 * Paleta de avatares. El color se elige por el id del usuario, asi que una
 * persona conserva siempre el mismo tono en todas las pantallas.
 *
 * Los tonos estan calibrados para que las iniciales en blanco encima superen
 * el minimo de contraste (4.5:1); los equivalentes mas vivos se leian mal.
 */
const AVATAR_COLORS = ['#0f766e', '#147a5a', '#9a6410', '#a8348a', '#2f6dc0', '#7a43d6'];

export function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Texto legible encima de un color arbitrario.
 *
 * Los colores de proyecto los elige la persona que lo crea, asi que no se puede
 * fijar el color del texto de antemano: se mide la luminancia del fondo y se
 * devuelve el extremo que mas contraste da.
 */
export function readableOn(background: string): string {
  const hex = background.replace('#', '');
  if (hex.length < 6) {
    return '#ffffff';
  }
  const channel = (start: number) => {
    const v = parseInt(hex.slice(start, start + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  // Se compara contra blanco y contra el negro de la marca, y gana el mejor.
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithInk = (luminance + 0.05) / 0.0577;
  return contrastWithWhite >= contrastWithInk ? '#ffffff' : '#04201c';
}

/** Iniciales a partir del nombre: "Ana Torres" -> "AT". */
export function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function personLabel(user: UserSummary | null | undefined): string {
  return user?.fullName ?? 'Sin asignar';
}

/**
 * "3 – 17 sep", el formato de rango que usa el diseno.
 *
 * Las fechas de un sprint son dias del calendario, no instantes: se guardan a
 * medianoche UTC. Si se formatean en la zona del navegador, cualquiera al oeste
 * de Greenwich las ve un dia antes (un sprint que empieza el 1 de septiembre
 * apareceria como 31 de agosto), asi que se leen y se pintan en UTC.
 */
const dayMonth = new Intl.DateTimeFormat('es', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

export function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) {
    return 'Sin fechas';
  }
  if (start && end) {
    const from = new Date(start);
    const to = new Date(end);
    // Si comparten mes solo se nombra el mes una vez.
    const sameMonth =
      from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
    return sameMonth
      ? `${from.getUTCDate()} – ${dayMonth.format(to)}`
      : `${dayMonth.format(from)} – ${dayMonth.format(to)}`;
  }
  return dayMonth.format(new Date((start ?? end)!));
}

export function daysRemaining(end: string | null): number | null {
  if (!end) {
    return null;
  }
  const diff = new Date(end).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

/** "09:42" para hoy, "ayer 16:40" para ayer, fecha corta para el resto. */
export function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const time = new Intl.DateTimeFormat('es', { hour: '2-digit', minute: '2-digit' }).format(date);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const stamp = date.getTime();

  if (stamp >= startOfToday) {
    return time;
  }
  if (stamp >= startOfToday - 86_400_000) {
    return `ayer ${time}`;
  }
  return new Intl.DateTimeFormat('es', { day: 'numeric', month: 'short' }).format(date);
}
